import { ResizeObserver } from '@juggle/resize-observer';
import { animated, useSpring } from '@react-spring/web';
import React, { ReactNode } from 'react';
import useMeasure from 'react-use-measure';
import styled, { css, useTheme } from 'styled-components';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

type Props = {
    children: ReactNode;
    onClick: () => void;
    selected: boolean;
    subtitle?: ReactNode;
    title: ReactNode;
};

const BOTTOM_MARGIN_HEIGHT = 32;

const PaymentMethod = ({ children, onClick, selected, subtitle, title }: Props) => {
    const {
        breakpoints: { tabletWidthNum },
    } = useTheme();
    const addBottomMargin = useIsNarrowerThan(tabletWidthNum + 1);
    const [contentContainerRef, { height }] = useMeasure({
        polyfill: ResizeObserver,
    });
    const slideInStyles = useSpring({
        to: {
            height: selected ? height + (addBottomMargin ? BOTTOM_MARGIN_HEIGHT : 0) : 0,
            opacity: selected ? 1 : 0,
            transform: `translate3d(${selected ? '0%' : '-100%'},0px,0px)`,
        },
    });
    return (
        <>
            <StyledPaymentMethodCard
                $selected={selected}
                onClick={onClick}
            >
                {title}
                {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
            </StyledPaymentMethodCard>
            <StyledContent style={slideInStyles}>
                <div ref={contentContainerRef}>{children}</div>
            </StyledContent>
        </>
    );
};

export default PaymentMethod;

const StyledPaymentMethodCard = styled.div<{ $selected: boolean }>`
    grid-row: tabs;
    height: 80px;
    max-width: 230px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
    padding: 16px;
    text-align: center;
    ${({ $selected, theme }) => {
        if ($selected) {
            return css`
                border: 3px solid ${theme.colors.blue100};
            `;
        } else {
            return css`
                cursor: pointer;
                border: 1px solid ${theme.colors.grey400};
                &:hover {
                    border: 3px solid ${({ theme }) => theme.colors.blue200};
                }
            `;
        }
    }};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        grid-row: auto;
        height: 72px;
        max-width: initial;
        align-items: flex-start;
        ${({ $selected }) => {
            if (!$selected) {
                return css`
                    margin-bottom: 8px;
                    margin-left: 2px;
                    &:hover {
                        margin-left: 0px;
                    }
                `;
            }
        }};
    }
`;

const StyledSubtitle = styled.div`
    margin-top: 4px;
`;

const StyledContent = styled(animated.div)`
    grid-area: content / 1 / -1 / -1;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletWidth}) {
        grid-area: auto;
    }
`;
